import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Bell,
  Download,
  Mail,
  Plus,
  Search,
  Settings,
  Trash2,
  Upload,
  Inbox,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip';
import { EmptyState } from '@/components/ui/empty-state';
import { KpiCard } from '@/components/dashboard/kpi-card-v2';
import { ComingSoonOverlay } from '@/components/dashboard/coming-soon-overlay';

export const Route = createFileRoute('/demos/component-showcase')({
  component: ComponentShowcase,
});

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-lg font-semibold text-fg border-b border-border pb-2 mb-4">
      {children}
    </h2>
  );
}

function ShowcaseCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-bg-surface p-4 space-y-3">
      <h3 className="text-sm font-medium text-fg-muted">{title}</h3>
      {children}
    </div>
  );
}

function ComponentShowcase() {
  const [switchValue, setSwitchValue] = useState(false);
  const [progressValue, setProgressValue] = useState(65);
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="min-h-screen bg-bg p-6 lg:p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-fg">Component Showcase</h1>
          <p className="text-fg-muted mt-1">
            Meridian Dark Design System — interactive component gallery
          </p>
        </div>

        {/* Buttons */}
        <section>
          <SectionTitle>Buttons</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Variants">
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
                <Button variant="danger">Danger</Button>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Sizes">
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="With Icons">
              <div className="flex flex-wrap gap-3">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create
                </Button>
                <Button variant="outline">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
                <Button variant="danger">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="States">
              <div className="flex flex-wrap gap-3">
                <Button isLoading>Loading</Button>
                <Button disabled>Disabled</Button>
              </div>
            </ShowcaseCard>
          </div>
        </section>

        {/* Badges */}
        <section>
          <SectionTitle>Badges</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Variants">
              <div className="flex flex-wrap gap-2">
                <Badge variant="default">Default</Badge>
                <Badge variant="primary">Primary</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="danger">Danger</Badge>
                <Badge variant="info">Info</Badge>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Shapes & Features">
              <div className="flex flex-wrap gap-2">
                <Badge shape="pill" variant="primary">
                  Pill
                </Badge>
                <Badge shape="square" variant="success">
                  Square
                </Badge>
                <Badge outline variant="warning">
                  Outline
                </Badge>
                <Badge dot variant="danger">
                  With Dot
                </Badge>
                <Badge pulse variant="info">
                  Pulse
                </Badge>
                <Badge icon={<Bell className="h-3 w-3" />} variant="primary">
                  Notification
                </Badge>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Sizes">
              <div className="flex flex-wrap items-center gap-2">
                <Badge size="xs">XS</Badge>
                <Badge size="sm">SM</Badge>
                <Badge size="md">MD</Badge>
                <Badge size="lg">LG</Badge>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Dismissible">
              <div className="flex flex-wrap gap-2">
                <Badge dismissible variant="primary">
                  Removable
                </Badge>
                <Badge dismissible variant="danger">
                  Close me
                </Badge>
              </div>
            </ShowcaseCard>
          </div>
        </section>

        {/* Cards */}
        <section>
          <SectionTitle>Cards</SectionTitle>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Basic Card</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-fg-muted text-sm">
                  Simple card with header and content sections.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Interactive Card</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-fg-muted text-sm mb-3">Card with actions inside.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline">
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stats Card</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-fg">1,234</div>
                <p className="text-fg-muted text-sm mt-1">Total records processed</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* KPI Cards */}
        <section>
          <SectionTitle>KPI Cards</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Total Calls"
              value="2,847"
              color="primary"
              trend={{ value: 12.5, direction: 'up', label: 'vs last month' }}
            />
            <KpiCard
              label="Avg Score"
              value="87%"
              color="success"
              trend={{ value: 3.2, direction: 'up' }}
            />
            <KpiCard
              label="Failed"
              value="23"
              color="danger"
              trend={{ value: -5.1, direction: 'down' }}
            />
            <KpiCard
              label="Pending"
              value="156"
              color="warning"
              tag="Active"
              tagColor="warning"
            />
          </div>
        </section>

        {/* Form Elements */}
        <section>
          <SectionTitle>Form Elements</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Inputs">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="demo-default">Default Input</Label>
                  <Input
                    id="demo-default"
                    placeholder="Type something..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="demo-search">With Icon</Label>
                  <Input
                    id="demo-search"
                    placeholder="Search..."
                    icon={<Search className="h-4 w-4" />}
                  />
                </div>
                <div>
                  <Label htmlFor="demo-error">Error State</Label>
                  <Input
                    id="demo-error"
                    placeholder="Invalid input"
                    error
                    defaultValue="bad-email"
                  />
                </div>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Switch & Toggle">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Notifications</Label>
                  <Switch checked={switchValue} onCheckedChange={setSwitchValue} />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Dark Mode (always on)</Label>
                  <Switch checked disabled />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Small Switch</Label>
                  <Switch size="sm" />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Large Switch</Label>
                  <Switch size="lg" />
                </div>
              </div>
            </ShowcaseCard>
          </div>
        </section>

        {/* Tabs */}
        <section>
          <SectionTitle>Tabs</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Default (Pill)">
              <Tabs defaultValue="overview">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="analytics">Analytics</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
                <TabsContent value="overview">
                  <p className="text-fg-muted text-sm p-2">Overview content</p>
                </TabsContent>
                <TabsContent value="analytics">
                  <p className="text-fg-muted text-sm p-2">Analytics content</p>
                </TabsContent>
                <TabsContent value="settings">
                  <p className="text-fg-muted text-sm p-2">Settings content</p>
                </TabsContent>
              </Tabs>
            </ShowcaseCard>

            <ShowcaseCard title="Underline">
              <Tabs defaultValue="calls">
                <TabsList variant="underline">
                  <TabsTrigger variant="underline" value="calls">
                    Calls
                  </TabsTrigger>
                  <TabsTrigger variant="underline" value="reports">
                    Reports
                  </TabsTrigger>
                  <TabsTrigger variant="underline" value="team">
                    Team
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="calls">
                  <p className="text-fg-muted text-sm p-2">Call records list</p>
                </TabsContent>
                <TabsContent value="reports">
                  <p className="text-fg-muted text-sm p-2">Reports dashboard</p>
                </TabsContent>
                <TabsContent value="team">
                  <p className="text-fg-muted text-sm p-2">Team members</p>
                </TabsContent>
              </Tabs>
            </ShowcaseCard>
          </div>
        </section>

        {/* Progress */}
        <section>
          <SectionTitle>Progress Bars</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Variants">
              <div className="space-y-3">
                <ProgressBar
                  value={progressValue}
                  variant="primary"
                  label="Primary"
                  showPercentage
                />
                <ProgressBar
                  value={80}
                  variant="success"
                  label="Success"
                  showPercentage
                />
                <ProgressBar
                  value={45}
                  variant="warning"
                  label="Warning"
                  showPercentage
                />
                <ProgressBar value={20} variant="danger" label="Danger" showPercentage />
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Sizes & States">
              <div className="space-y-3">
                <ProgressBar value={70} size="sm" label="Small" />
                <ProgressBar value={70} size="md" label="Medium" />
                <ProgressBar value={70} size="lg" label="Large" />
                <ProgressBar indeterminate label="Indeterminate" />
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Interactive">
              <div className="space-y-3">
                <ProgressBar
                  value={progressValue}
                  variant="primary"
                  label="Drag slider to change"
                  showPercentage
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={progressValue}
                  onChange={(e) => setProgressValue(Number(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
            </ShowcaseCard>
          </div>
        </section>

        {/* Tooltips */}
        <section>
          <SectionTitle>Tooltips</SectionTitle>
          <ShowcaseCard title="Hover to see">
            <TooltipProvider>
              <div className="flex flex-wrap gap-3">
                <Tooltip content="Send an email notification">
                  <Button variant="outline">
                    <Mail className="mr-2 h-4 w-4" />
                    Email
                  </Button>
                </Tooltip>

                <Tooltip content="Open application settings">
                  <Button variant="outline">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Button>
                </Tooltip>

                <Tooltip content="Upload audio files for analysis">
                  <Button variant="outline">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </Button>
                </Tooltip>
              </div>
            </TooltipProvider>
          </ShowcaseCard>
        </section>

        {/* Skeletons */}
        <section>
          <SectionTitle>Skeletons (Loading States)</SectionTitle>
          <div className="grid gap-4 md:grid-cols-3">
            <ShowcaseCard title="Text Lines">
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Card Skeleton">
              <div className="space-y-3">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-10 w-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Avatar & Text">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            </ShowcaseCard>
          </div>
        </section>

        {/* Empty State */}
        <section>
          <SectionTitle>Empty States</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="No Data">
              <EmptyState
                icon={Inbox}
                title="No records found"
                description="Start by uploading your first audio file for analysis."
                primaryAction={{
                  label: 'Upload File',
                  onClick: () => {},
                  icon: Upload,
                }}
              />
            </ShowcaseCard>

            <ShowcaseCard title="Search Empty">
              <EmptyState
                icon={Search}
                title="No results"
                description="Try adjusting your search or filter criteria."
              />
            </ShowcaseCard>
          </div>
        </section>

        {/* Coming Soon Overlay */}
        <section>
          <SectionTitle>Coming Soon Overlay</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Over a Card">
              <ComingSoonOverlay>
                <Card>
                  <CardHeader>
                    <CardTitle>Advanced Analytics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-fg-muted text-sm">
                      Deep insights and trend analysis coming in the next release.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm">View Report</Button>
                      <Button size="sm" variant="outline">
                        Export
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </ComingSoonOverlay>
            </ShowcaseCard>

            <ShowcaseCard title="Over KPI">
              <ComingSoonOverlay>
                <KpiCard
                  label="Sentiment Score"
                  value="92%"
                  color="success"
                  trend={{ value: 8.3, direction: 'up' }}
                />
              </ComingSoonOverlay>
            </ShowcaseCard>
          </div>
        </section>

        {/* Color Palette */}
        <section>
          <SectionTitle>Color Palette</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2">
            <ShowcaseCard title="Backgrounds">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded border border-border bg-bg" />
                  <span className="text-sm text-fg-muted">bg-bg (Base)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded border border-border bg-bg-elevated" />
                  <span className="text-sm text-fg-muted">bg-bg-elevated</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded border border-border bg-bg-surface" />
                  <span className="text-sm text-fg-muted">bg-bg-surface</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded border border-border bg-bg-muted" />
                  <span className="text-sm text-fg-muted">bg-bg-muted</span>
                </div>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Semantic Colors">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-primary" />
                  <span className="text-sm text-fg-muted">Primary (Teal)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-success" />
                  <span className="text-sm text-fg-muted">Success</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-warning" />
                  <span className="text-sm text-fg-muted">Warning</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-danger" />
                  <span className="text-sm text-fg-muted">Danger</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-info" />
                  <span className="text-sm text-fg-muted">Info</span>
                </div>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Text Colors">
              <div className="space-y-2">
                <p className="text-fg text-sm font-medium">text-fg — Primary text</p>
                <p className="text-fg-muted text-sm">text-fg-muted — Secondary text</p>
                <p className="text-fg-subtle text-sm">text-fg-subtle — Tertiary text</p>
                <p className="text-primary text-sm font-medium">
                  text-primary — Accent text
                </p>
                <p className="text-success text-sm">text-success — Success text</p>
                <p className="text-warning text-sm">text-warning — Warning text</p>
                <p className="text-danger text-sm">text-danger — Danger text</p>
              </div>
            </ShowcaseCard>

            <ShowcaseCard title="Spacing Scale">
              <div className="space-y-1">
                {[1, 2, 3, 4, 6, 8, 10, 12, 16].map((s) => (
                  <div key={s} className="flex items-center gap-3">
                    <div
                      className="h-3 bg-primary/40 rounded"
                      style={{ width: `${s * 4}px` }}
                    />
                    <span className="text-xs text-fg-muted font-mono">
                      {s} ({s * 4}px)
                    </span>
                  </div>
                ))}
              </div>
            </ShowcaseCard>
          </div>
        </section>
      </div>
    </div>
  );
}
